import {Component, HostListener, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {AlarmEvent, AlarmStage, AlarmValue, AutoClearSetting, LORAWAN_GATEWAY_INACTIVE, LorawanGatewayEventInfo, ValueQueryResult} from '../../../../shared/models/alarm';
import {AlarmsService} from '../../../../core/services/alarms.service';
import {TilesService} from '../../../../core/services/tiles.service';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {BehaviorSubject, Observable, Subscription} from 'rxjs';
import {filter, map} from 'rxjs/operators';
import {Tile} from '../../../../shared/models/tile';
import {TitleBreadcrumbsChangedAction, TitleChangedAction} from '../../../../core/actions/title.actions';
import {Store} from '@ngrx/store';
import {AppState} from '../../../../app/app.state';
import {User} from '../../../../shared/models/user';
import {UsersService} from '../../../../core/services/users.service';
import {SortTable, SortTableRow} from '../../../../shared/models/sort-table/types';
import {SortTableCellTextPill} from '../../../../shared/models/sort-table/cell-classes';
import {UtilsService} from '../../../../core/services/utils.service';
import {TileValueParser} from '../../../../shared/models/tile-value-parser';
import {TileHistoryService} from '../../../../core/services/tile-history.service';
import {TileDataset} from '../../../../shared/models/tile-dataset';
import {HUB_FETCH_INTERVAL, HubsService} from '../../../../core/services/hubs.service';
import {DevicesService} from '../../../../core/services/devices.service';
import {Device} from '../../../../shared/models/device';
import {Hub} from '../../../../shared/models/hub';
import {ObjectType} from '../../../../shared/models/object.model';
import {MotiChatService} from '@moti/services/moti-chat.service';
import {PermissionsService} from '../../../../core/services/permissions.service';
import {Permission} from '../../../../shared/models/fga/permission';
import {SessionService} from '../../../../core/services/session.service';
import {SimpleStructure} from '../../../../shared/models/structure';
import {HubStatus} from '../../../../shared/models/hub-status';
import {SpinnerComponent} from '../../../components/spinner/spinner.component';
import {StatStringComponent} from '../../../components/stat-string/stat-string.component';
import {TileComponent} from '../../../components/tile/tile.component';
import {TileHistoryComponent} from '../../../components/tile-history/tile-history.component';
import {SortTableComponent} from '../../../components/sort-table/sort-table.component';
import {PillComponent} from '../../../components/pill/pill.component';
import {PillLetter} from '../../../../shared/models/pill';

const COL_IDX_TIME = 0;
const COL_IDX_VALUE = 1;
const COL_IDX_UUID = 2;

const FETCH_QTY = 25;

@Component({
  selector: 'motn-alarm-event-details',
  templateUrl: './alarm-event-details.component.html',
  styleUrls: ['./alarm-event-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    RouterLink,
    SpinnerComponent,
    StatStringComponent,
    TileComponent,
    TileHistoryComponent,
    SortTableComponent,
    PillComponent,
  ],
})
export class AlarmEventDetailsComponent implements OnInit, OnDestroy {
  event: AlarmEvent;
  eventUuid: string;
  newestEventUuid: string = ''; // if the same object has active alarm that isn't this alarm event

  tableValues: ValueQueryResult;
  newestValue: AlarmValue;
  page: number = 1;
  qty: number = FETCH_QTY;

  tile: Tile;
  hub: Hub;
  device: Device;
  ObjectType = ObjectType;

  hubStatus: HubStatus;
  private hubStatusSub: Subscription;
  private hubStatusTimeout: ReturnType<typeof setTimeout>;

  gatewayConnected: boolean | null = null;
  private lorawanGatewaySub: Subscription;
  private lorawanGatewayTimeout: ReturnType<typeof setTimeout>;

  objectName: string = '';
  canChat: boolean = false;

  chartData$: BehaviorSubject<TileDataset[]>;
  private chartTimeout: number = 0;
  private tileValueParser: TileValueParser;

  private routeSub: Subscription;
  private tilesSub: Subscription;
  private hubsSub: Subscription;
  private devicesSub: Subscription;
  private alarmsSub: Subscription;
  private alarmsSub2: Subscription;

  ackUser: User;
  clearUser: User;

  table$: BehaviorSubject<SortTable> = new BehaviorSubject<SortTable>(null);

  viewIsVisible: boolean = true;
  userInterventionEnabled: boolean = false; // starts false for several hundred milliseconds to avoid accidental clicks

  @HostListener('document:visibilitychange')
  visibilitychange() {
    if (document.hidden) {
      this.viewIsVisible = false;
    } else {
      this.viewIsVisible = true;
    }
  }


  constructor(
    private alarmsService: AlarmsService,
    private tilesService: TilesService,
    private hubsService: HubsService,
    private devicesService: DevicesService,
    private tileHistoryService: TileHistoryService,
    private usersService: UsersService,
    private route: ActivatedRoute,
    private store: Store<AppState>,
    private motiChatService: MotiChatService,
    private permissionsService: PermissionsService,
    private session: SessionService,
  ) {
  }


  openChat() {
    this.motiChatService.openChat(this.event.ObjectID, this.objectName, this.event.ObjectType);
  }


  private checkChatPermission() {
    this.permissionsService.queryPermissions({
      UserID: this.session.userId,
      ObjectID: this.event.ObjectID,
      ObjectType: this.event.ObjectType,
      Permissions: [Permission.CAN_CHAT],
    }).subscribe(response => {
      this.canChat = response.Results.find(r => r.Permission === Permission.CAN_CHAT)?.Allowed || false;
      this.updateTitle();
    });
  }


  ngOnInit(): void {
    this.routeSub = this.route.paramMap.subscribe((params) => {
      this.eventUuid = params.get('eventUuid');
      this.ackUser = null;
      this.clearUser = null;

      this.updateTitle();
      this.preventAccidentalIntervention();

      this.alarmsSub?.unsubscribe();

      this.fetchEvent$().subscribe({
        next: () => {
          this.alarmsSub = this.alarmsService.activeEvents$.pipe(
            map((e) => {
              return e.find(e => e.UUID === this.eventUuid);
            }),
          ).subscribe({
            next: (alarmEvent) => {
              // Alarm events that are active/uncleared should satisfy
              // this condition
              if (alarmEvent) {
                this.event = alarmEvent;
                this.subscribeToSubject();
                this.fetchUsers();
                this.fetchAlarmValues();
              } else {
                // If the alarm became cleared after the view was initially
                // loaded, ClearTime will be falsey and the activeEvents$
                // observable won't contain an updated event, so we have to
                // go manually fetch this alarm event one more time to
                // obtain clearance details
                if (!this.event?.ClearTime) {
                  this.fetchEvent$().subscribe();
                }
              }
            },
          });
        },
      });
    });
  }


  ngOnDestroy() {
    clearTimeout(this.chartTimeout);
    clearTimeout(this.hubStatusTimeout);
    clearTimeout(this.lorawanGatewayTimeout);

    this.alarmsSub?.unsubscribe();
    this.alarmsSub2?.unsubscribe();
    this.routeSub?.unsubscribe();
    this.tilesSub?.unsubscribe();
    this.hubsSub?.unsubscribe();
    this.devicesSub?.unsubscribe();
    this.hubStatusSub?.unsubscribe();
    this.lorawanGatewaySub?.unsubscribe();
  }


  /**
   * If a user lands on the event details view with their cursor right above
   * an intervention action button, this prevents an accidental click for the
   * first several hundred milliseconds of the view's being shown.
   */
  preventAccidentalIntervention() {
    this.userInterventionEnabled = false;

    setTimeout(() => {
      this.userInterventionEnabled = true;
    }, 500);
  }


  makePrettyTime(time: string): string {
    return UtilsService.makeMonospaceTime(time);
  }


  get currentStage(): AlarmStage {
    if (this.event) {
      if (this.event.ClearTime) {
        return 'cleared';
      }

      if (this.event.AckTime) {
        return 'acked';
      }

      return 'unacked';
    }
  }


  get isLorawanGatewayAlarm(): boolean {
    return (this.event?.EventInfo as LorawanGatewayEventInfo)?.Category === LORAWAN_GATEWAY_INACTIVE;
  }


  get gatewayEUI(): string {
    return this.event?.EventInfo?.LoRaWAN?.GatewayEUI ?? this.event?.IdempotencyKey?.split(':')?.[1] ?? '';
  }


  get interventionText(): string {
    let text: string = '';
    let autoClearSetting: AutoClearSetting;


    switch (this.event.ObjectType) {
      case ObjectType.TILE:
        autoClearSetting = (this.tile?.Structure as SimpleStructure)?.AutoClear;

        if (this.currentStage === 'acked') {
          if (autoClearSetting === '') {
            text = `Waiting for clearance by user`;
          } else {
            text = `Waiting for auto-clearance or clearance by user`;
          }
        } else {
          if (autoClearSetting === 'all') {
            text = `Waiting for auto-clearance or user intervention`;
          } else {
            text = `Waiting for user intervention`;
          }
        }

        break;
      case ObjectType.HUB:
        if (this.isLorawanGatewayAlarm) {
          if (this.currentStage === 'acked') {
            text = `Waiting for gateway to reconnect or clearance by user`;
          } else {
            text = `Waiting for gateway to reconnect or user intervention`;
          }
        } else {
          if (this.currentStage === 'acked') {
            text = `Waiting for hub to reconnect or clearance by user`;
          } else {
            text = `Waiting for hub to reconnect or user intervention`;
          }
        }

        break;
      case ObjectType.DEVICE:
        let snippet: string;
        autoClearSetting = this.device?.Config.AutoClear ?? '';

        const readErrors = this.newestValue?.Data.ReadErrors;
        const writeErrors = this.newestValue?.Data.WriteErrors;

        if (readErrors && writeErrors) {
          snippet = `successful read and write operations`;
        } else if (this.newestValue?.Data.ReadErrors) {
          snippet = `a successful read operation`;
        } else if (this.newestValue?.Data.WriteErrors) {
          snippet = `a successful write operation`;
        }

        if (this.currentStage === 'acked') {
          if (autoClearSetting === '') {
            text = `Waiting for clearance by user`;
          } else {
            text = `Waiting for ${snippet} or clearance by user`;
          }
        } else {
          if (autoClearSetting === 'all') {
            text = `Waiting for ${snippet} or user intervention`;
          } else {
            text = `Waiting for user intervention`;
          }
        }

        break;
      default:
    }

    return text;
  }


  acknowledgeAlarm() {
    if (!this.userInterventionEnabled) {
      return;
    }

    this.alarmsService.acknowledgeEvent$(this.eventUuid).subscribe();
  }


  clearAlarm() {
    if (!this.userInterventionEnabled) {
      return;
    }

    this.alarmsService.clearEvent$(this.eventUuid).subscribe();
  }


  changePageNumber(pageNumber: number) {
    this.page = pageNumber;
    this.fetchAlarmValues();
  }


  showMoreData() {
    this.tileHistoryService.addToDrawerTiles([this.tile]);
  }


  private fetchEvent$(): Observable<null> {
    return new Observable(subscriber => {
      this.alarmsService.fetchAlarmEvent$(this.eventUuid).subscribe({
        next: (alarmEvent) => {
          this.event = alarmEvent;
          this.fetchUsers();
          this.subscribeToSubject();

          subscriber.next(null);
          subscriber.complete();
        },
        error: () => {
          subscriber.error();
        },
      });
    });
  }


  private fetchAlarmValues() {
    if (this.viewIsVisible) {
      this.alarmsService.fetchAlarmValues$({
        EventUUID: this.eventUuid,
        Page: '' + this.page,
        Qty: '' + this.qty,
      }).subscribe({
        next: (result) => {
          this.tableValues = result;
          this.updateTable();

          if (this.page === 1) {
            this.newestValue = result.ResultSet[0];
          }
        },
      });
    }

    if (this.page > 1) {
      this.alarmsService.fetchAlarmValues$({
        EventUUID: this.eventUuid,
        Page: '' + 1,
        Qty: '' + 1,
      }).subscribe({
        next: (result) => {
          this.newestValue = result.ResultSet[0];
        },
      });
    }
  }


  private fetchTileChartHistory() {
    this.tileHistoryService.fetchHistory$(this.tile).subscribe(
      (ds) => {
        if (!this.chartData$) {
          this.chartData$ = new BehaviorSubject<TileDataset[]>([ds]);
        } else {
          this.chartData$.next([ds]);
        }

        const fn = () => {
          this.fetchTileChartHistory();
        };

        if (this.chartTimeout) {
          clearTimeout(this.chartTimeout);
        }

        this.chartTimeout = window.setTimeout(fn, 15000);
      },
    );
  }


  private fetchUsers() {
    if (this.event.AckUser && this.event.AckUser !== '0' && !this.ackUser) {
      this.usersService.fetchUser$(this.event.AckUser).subscribe(
        (u) => {
          this.ackUser = u;
        },
      );
    }

    if (this.event.ClearUser && this.event.ClearUser !== '0' && !this.clearUser) {
      this.usersService.fetchUser$(this.event.ClearUser).subscribe(
        (u) => {
          this.clearUser = u;
        },
      );
    }
  }


  private subscribeToSubject() {
    if (!this.event?.ObjectID) {
      return;
    }

    switch (this.event.ObjectType) {
      case ObjectType.TILE:
        this._subscribeToTile();
        break;
      case ObjectType.HUB:
        this._subscribeToHub();
        break;
      case ObjectType.DEVICE:
        this._subscribeToDevice();
        break;
      default:
    }
  }


  private _subscribeToTile() {
    this.tilesSub?.unsubscribe();

    this.tilesSub = this.tilesService.sessUserTiles$.pipe(
      map(tiles => tiles[this.event?.ObjectID]),
      filter(t => !!t),
    ).subscribe({
      next: (tile) => {
        this.tile = tile;
        this.objectName = tile.displayName;

        this.checkChatPermission();
        this.fetchTileChartHistory();

        this._subscribeToNewerSubjectAlarms(ObjectType.TILE);
      },
    });
  }


  private _subscribeToHub() {
    this.hubsSub?.unsubscribe();

    this.hubsSub = this.hubsService.sessUserHubs$.pipe(
      map(hubs => hubs[this.event?.ObjectID]),
      filter(h => !!h),
    ).subscribe({
      next: (hub) => {
        this.hub = hub;
        this.objectName = hub.Name;

        this.checkChatPermission();
        this._subscribeToNewerSubjectAlarms(ObjectType.HUB);

        // Only poll tunnel-test for non-LoRaWAN gateway alarms.
        // For LoRaWAN gateway alarms, poll the lorawan-gateways endpoint instead.
        if (this.isLorawanGatewayAlarm) {
          if (this.gatewayEUI && !this.lorawanGatewaySub) {
            this.fetchLorawanGatewayStatus();
          }
          // No tunnel-test polling for LoRaWAN gateway alarms — hub is cloud-hosted
        } else {
          if (!this.hubStatusSub) {
            this.fetchHubStatus();
          }
        }
      },
    });

    this.hubsService.getUserHubs$();
  }


  private _subscribeToDevice() {
    this.devicesSub?.unsubscribe();

    this.devicesSub = this.devicesService.sessUserDevices$.pipe(
      map(devices => devices[this.event?.ObjectID]),
      filter(d => !!d),
    ).subscribe({
      next: (device) => {
        this.device = device;
        this.objectName = device.Name;

        this.checkChatPermission();
        this._subscribeToNewerSubjectAlarms(ObjectType.DEVICE);
      },
    });

    this.devicesService.getUserDevices$();
  }


  // For LoRaWAN gateway alarms, a "newer" alarm must be for the same gateway —
  // otherwise a different gateway going offline would incorrectly trigger Heads Up.
  // Non-LoRaWAN alarms skip this check entirely.
  private _isSameGatewayOrNotLoraWAN(e: AlarmEvent): boolean {
    if (!this.isLorawanGatewayAlarm) return true;
    const eui = e.EventInfo?.LoRaWAN?.GatewayEUI ?? e.IdempotencyKey?.split(':')?.[1];
    return eui === this.gatewayEUI;
  }

  private _subscribeToNewerSubjectAlarms(subjectType: ObjectType) {
    this.alarmsSub2?.unsubscribe();
    this.alarmsSub2 = this.alarmsService.activeEvents$.pipe(
      map((e) => {
        return e.find(e =>
          e.ObjectType === subjectType &&
          e.ObjectID === this.event.ObjectID &&
          e.StartTime > this.event?.StartTime &&
          this._isSameGatewayOrNotLoraWAN(e),
        );
      }),
      filter(e => !!e),
    ).subscribe(
      (alarmEvent) => {
        this.newestEventUuid = alarmEvent.UUID;
      },
    );
  }


  private fetchHubStatus() {
    clearTimeout(this.hubStatusTimeout);

    this.hubStatusSub = this.hubsService.fetchHubStatus$(this.hub.ID).subscribe({
      next: (status) => {
        this.hubStatus = status;

        this.hubStatusTimeout = setTimeout(() => {
          this.fetchHubStatus();
        }, HUB_FETCH_INTERVAL);
      },
    });
  }


  private fetchLorawanGatewayStatus() {
    clearTimeout(this.lorawanGatewayTimeout);

    this.lorawanGatewaySub = this.hubsService.fetchLorawanGateways$(this.hub.ID).subscribe({
      next: (gateways) => {
        const gateway = gateways.find(g => g.EUI === this.gatewayEUI);

        if (gateway) {
          this.gatewayConnected = !!(
            gateway.LastReceivedAt &&
            gateway.LastAlarmedAt &&
            gateway.LastReceivedAt > gateway.LastAlarmedAt
          );
        } else {
          this.gatewayConnected = null;
        }

        this.lorawanGatewayTimeout = setTimeout(() => {
          this.fetchLorawanGatewayStatus();
        }, HUB_FETCH_INTERVAL);
      },
    });
  }


  private updateTable() {
    const colTimeWidth = 260;
    const colValueWidth = 150;
    const colUuidWidth = 300;

    const table: SortTable = {
      css: {
        'min-width': (colTimeWidth + colValueWidth + colUuidWidth) + 'px',
      },
      columns: [],
      rows: [],
      pagination: {
        enabled: true,
        rowsPerPage: this.qty,
        startOnPage: 1,
        maxPageOptions: 12,
        totalRowQty: this.tableValues.Count,
      },
    };

    {
      table.columns[COL_IDX_TIME] = {
        name: 'Time Recorded',
        css: {
          'width': colTimeWidth + 'px',
        },
      };

      table.columns[COL_IDX_VALUE] = {
        name: 'Alarmable Value',
        css: {
          'width': `calc(100% - ${colTimeWidth + colUuidWidth}px)`,
          'min-width': colValueWidth + 'px',
          'justify-content': 'flex-start',
        },
      };

      table.columns[COL_IDX_UUID] = {
        name: 'Value UUID',
        css: {
          'width': colUuidWidth + 'px',
          'justify-content': 'flex-end',
        },
        infoDialogText: 'A unique ID is generated for every recorded value that satisfies an alarm condition.',
      };
    }

    for (let value of this.tableValues.ResultSet) {
      const row: SortTableRow = {
        cells: [],
        context: value.UUID,
      };

      this.upsertRowData(row, value);
      table.rows.push(row);
    }

    this.table$.next(table);
  }


  private upsertRowData(row: SortTableRow, value: AlarmValue) {
    if (this.tile && !this.tileValueParser) {
      this.tileValueParser = new TileValueParser(this.tile, 0);
    }

    let displayValue;

    switch (this.event?.ObjectType) {
      case ObjectType.TILE: {
        if (this.tileValueParser) {
          displayValue = this.tileValueParser.getDisplayValue(value.Data.Value);
        } else {
          displayValue = value.Data.Value;
        }

        break;
      }
      case ObjectType.HUB: {
        break;
      }
      case ObjectType.DEVICE: {
        displayValue = `${value.Data.ReadErrors} Read / ${value.Data.WriteErrors} Write Failures`;

        break;
      }
      default: {
        return;
      }
    }

    row.cells[COL_IDX_TIME] = new SortTableCellTextPill(UtilsService.makeMonospaceTime(value.Time), '', true);
    row.cells[COL_IDX_VALUE] = new SortTableCellTextPill(displayValue, '');
    row.cells[COL_IDX_UUID] = new SortTableCellTextPill(value.UUID, '', true);
  }


  /**
   *
   * @private
   */
  private updateTitle() {
    const subtitle: {text: string; icon?: string; callback?: () => void}[] = [
      {text: `Event ID ${this.eventUuid}`},
    ];

    if (this.canChat) {
      subtitle.push({text: 'Chat', icon: 'kit:chat', callback: () => this.openChat()});
    }

    this.store.dispatch(new TitleChangedAction('', 'Alarm Details', subtitle));

    this.store.dispatch(new TitleBreadcrumbsChangedAction(
      [
        {
          text: 'Alarms',
        },
        {
          text: 'Events',
        },
      ],
    ));
  }

  get isSubjectTypeValidForTable(): boolean {
    return [ObjectType.TILE, ObjectType.DEVICE].indexOf(this.event?.ObjectType) > -1;
  }

  get shouldShowTable(): boolean {
    return this.table$ && this.isSubjectTypeValidForTable;
  }

  get subjectTypeFirstLetter(): PillLetter {
    return this.event?.ObjectType?.[0] as PillLetter ?? '';
  }
}
